<template>
  <div
    id="app"
    ref="app"
    :class="{ 'rs-app-iframe': isIFrame }"
  >
    <RsPartialIFrameLoadingScreen v-if="!isAuthChecked && isIFrame" />

    <div
      v-if="!isAuthChecked && !isIFrame"
      class="app-splash d-flex flex-column justify-content-center align-items-center"
    >
      <img src="/img/rentsync_logo.png" />
      <div class="mt-4">
        <i class="fas fa-2x fa-spin fa-spinner" />
      </div>
    </div>

    <RsToastUndo
      v-for="(toast, index) in undoToasts"
      :id="`toast-remove-${index}`"
      :key="index"
      v-model="toast.show"
      :title-text="toast.config.titleText"
      :body-text="toast.config.bodyText"
      :delay="toast.config.delay"
      :toast-data="toast.config.toastData"
      :init-callback="toast.config.initCallback"
      :complete-callback="toast.config.completeCallback"
      :undo-callback="toast.config.undoCallback"
    />

    <div v-if="appLoaded && $route.meta?.hideBlade">
      <div
        v-show="$store.state.loading"
        class="rs-main-spin h-100"
      >
        <RsSpin />
      </div>
      <RouterView />
    </div>

    <div
      v-if="appLoaded && !$route.meta?.hideBlade"
      class="h-100"
    >
      <RsLayoutAppV2
        @on-modal-account-trigger="isModalAccountOpen = true"
        @on-logout-trigger="handleLogout"
      />

      <RsModalAccount
        v-model="isModalAccountOpen"
        :user="$rsGlobals.$currentUser"
        @on-forgot-password="handleForgotPassword"
      />

      <RsModalValidationSummary />
    </div>
  </div>
</template>

<script lang="ts">
import { RouteLocationNormalized } from 'vue-router';
import { Handler } from 'mitt';
import { defineComponent, h } from 'vue';
import { mapModuleActions, mapModuleGetters } from '@/shared/vuex-toolkit';
import { main, users as userModule } from '@/store/typed/typed-modules';
import { TLanguage } from '@/shared';
import { IToastUndoConfig } from '@/shared/interfaces/toast-undo-config';
import { ISidebarHeaderConfig } from '@/shared/interfaces/sidebar-header-config';
import users, { loginWithSso } from '@/shared/services/users';
import { sidebarHeaderConfigs } from '@/shared/helpers/sidebar-header-configs';
import RsLayoutAppV2 from '@/components/RsLayoutAppV2.vue';
import RsModalAccount from '@/components/RsModalAccount.vue';
import RsModalValidationSummary from '@/components/RsModalValidationSummary.vue';
import { initGtag } from '@/shared/helpers/init-gtag';
import { clientIdPollIntervalMS } from './config';
import RsPartialIFrameLoadingScreen from '@/components/RsPartialIFrameLoadingScreen.vue';

const toastContent = (title: string, message: string) => ({
  render: () => h('div', [
    h('div', { class: 'Vue-Toastification__toast-title' }, title),
    h('div', message),
  ]),
});

/**
 * Returns true when `origin` matches any entry in `patterns`. Entries prefixed with `*.`
 * are treated as subdomain wildcards — `*.dev.building-stack.com` matches every direct
 * subdomain of dev.building-stack.com. All other entries require an exact match.
 */
const isOriginTrusted = (origin: string, patterns: string[]): boolean => patterns.some((pattern) => {
  if (pattern.startsWith('*.')) return origin.endsWith(pattern.slice(1));
  return origin === pattern;
});

export default defineComponent({
  name: 'App',

  components: {
    RsLayoutAppV2,
    RsModalAccount,
    RsModalValidationSummary,
    RsPartialIFrameLoadingScreen,
  },

  data() {
    return {
      isModalAccountOpen: false,
      appLoaded: false,
      progressToastId: null as string | number | null,
      defaultToast: {
        show: false,
        config: {
          titleText: `Removal in progress`,
          bodyText: `Removing item`,
          delay: 8000,
          currentState: null,
          // eslint-disable-next-line no-console
          initCallback: () => console.log('Removal begin'),
          // eslint-disable-next-line no-console
          completeCallback: () => console.log('Removal complete'),
          // eslint-disable-next-line no-console
          undoCallback: () => console.log('Removal undone'),
        } as IToastUndoConfig,
      },
      undoToasts: [] as Array<{ show: boolean; config: IToastUndoConfig }>,
      trustedIFrameOrigins: this.$rsGlobals.$env.VUE_APP_IFRAME_ANCESTOR_WHITELIST
        ? this.$rsGlobals.$env.VUE_APP_IFRAME_ANCESTOR_WHITELIST
          .split(' ')
          .map((url: string) => url.replace(/\/+$/, '')) // Removes one or more trailing slashes
        : [],
    };
  },

  computed: {
    ...mapModuleGetters(main, ['sidebarHeaderData', 'userSettings']),
    ...mapModuleGetters(userModule, ['isAuthChecked']),
    isIFrame(): boolean {
      return this.$rsGlobals.$isIFrame;
    },
  },

  watch: {
    $route(newValue: RouteLocationNormalized, oldValue: RouteLocationNormalized) {
      this.appLoaded = true;
      this.processSidebarHeader(newValue, oldValue);
    },
    isAuthChecked(newVal: boolean) {
      if (newVal) {
        this.ensureClientIdStored();
      }
    },
  },

  mounted() {
    this.initApp();
  },

  beforeUnmount() {
    document.removeEventListener('visibilitychange', this.handleSyncEvents);
    window.removeEventListener('storage', this.handleSyncEvents);
  },

  methods: {
    ...mapModuleActions(userModule, ['getCurrentClient', 'saveSelf', 'changeCurrentClient']),
    async initApp() {
      initGtag();

      window.addEventListener('message', this.handleSsoMessage);
      // Since we have multiple possible parents, we can't hardcode one URL here.
      // Using '*' is acceptable for the 'READY' signal because we aren't sending sensitive data, only requesting it.
      window.parent.postMessage({ type: 'IFRAME_READY' }, '*');

      this.$eventBus.on('on-api-error', ((data: { message?: string }) => {
        this.hideProgressToast();
        this.showErrorToast(this.$ts('toast.errorTitle'), data?.message);
      }) as Handler);

      this.$eventBus.on('show-progress-toast', ((data?: { title?: string; message?: string }) => {
        this.showProgressToast(data?.title, data?.message);
      }) as Handler);

      this.$eventBus.on('hide-progress-toast', () => {
        this.hideProgressToast();
      });

      this.$eventBus.on('show-success-toast', ((data?: { title?: string; message?: string }) => {
        this.showSuccessToast(data?.title, data?.message);
      }) as Handler);

      this.$eventBus.on('show-error-toast', ((data?: { title?: string; message?: string }) => {
        this.showErrorToast(data?.title, data?.message);
      }) as Handler);

      this.$eventBus.on('show-warning-toast', ((data?: { title?: string; message?: string }) => {
        this.showWarningToast(data?.title, data?.message);
      }) as Handler);

      this.$eventBus.on('set-loading', ((data: boolean) => {
        if (main.getters.loading() !== data) {
          main.actions.setLoading(data);
        }
      }) as Handler);

      this.$eventBus.on('set-language', ((language: TLanguage) => {
        if ((this.$rsGlobals.$language === 'en' && language !== 'en') || language !== this.$rsGlobals.$language) {
          main.actions.setLanguage(language);
          // eslint-disable-next-line no-console
          console.log('--> Language Set - ', this.$rsGlobals.$language);
        }
      }) as Handler);

      this.$eventBus.on('on-show-undo-toast', ((data: IToastUndoConfig) => {
        this.showUndoToast(data);
      }) as Handler);
      this.initClientIdSync();
    },

    async onClientSelect(data: { id: string | number }) {
      await this.changeCurrentClient(Number(data.id));

      try {
        if (this.$rsGlobals?.$currentUser?.client?.id) {
          window.localStorage.setItem('currentClientId', String(this.$rsGlobals.$currentUser.client.id));
        }
      } catch (error) {
        console.error('Failed to update client ID in localStorage:', error);
      }

      // For simplicity, use the Javascript location object,
      // as Vue's router will not reliably remove query strings and fully reset state
      if (Object.keys(this.$route.params || {}).length > 0) {
        window.location.replace('/');
      } else {
        window.location.replace(this.$route.path);
      }
    },

    handleLogout() {
      users.logout();
    },

    showProgressToast(title?: string, message?: string) {
      const resolvedTitle = title ?? this.$ts('toast.progressTitle');
      const resolvedMessage = message ?? this.$ts('toast.progressMessage');
      this.progressToastId = this.$toast.info(toastContent(resolvedTitle, resolvedMessage), { timeout: false, closeOnClick: false, draggable: false });
    },

    showSuccessToast(title?: string, message?: string) {
      const resolvedTitle = title ?? this.$ts('toast.successTitle');
      const resolvedMessage = message ?? this.$ts('toast.successMessage');
      this.hideProgressToast();
      this.$toast.success(toastContent(resolvedTitle, resolvedMessage), { timeout: 4000 });
    },

    showErrorToast(title?: string, message?: string | string[]) {
      const resolvedTitle = title ?? this.$ts('toast.errorTitle');
      const resolvedMessage = message ?? this.$ts('toast.errorMessage');
      this.hideProgressToast();
      this.$toast.clear();
      this.$nextTick(() => {
        if (Array.isArray(resolvedMessage)) {
          resolvedMessage.forEach((msg) => this.$toast.error(toastContent(resolvedTitle, msg), { timeout: 6000 }));
        } else {
          this.$toast.error(toastContent(resolvedTitle, resolvedMessage), { timeout: 6000 });
        }
      });
    },

    showWarningToast(title?: string, message?: string) {
      const resolvedTitle = title ?? this.$ts('toast.warningTitle');
      const resolvedMessage = message ?? this.$ts('toast.warningMessage');
      this.hideProgressToast();
      this.$toast.warning(toastContent(resolvedTitle, resolvedMessage), { timeout: 4000 });
    },

    showUndoToast(config: IToastUndoConfig) {
      const toast: { show: boolean; config: IToastUndoConfig } = {
        ...this.defaultToast,
        ...{
          show: true,
          config: { ...this.defaultToast.config, ...config },
        },
      };

      this.$nextTick(() => {
        this.undoToasts.push(toast);
      });
    },

    hideProgressToast() {
      if (this.progressToastId !== null) {
        this.$toast.dismiss(this.progressToastId);
        this.progressToastId = null;
      }
    },

    handleForgotPassword() {
      this.isModalAccountOpen = false;
      this.$router.push('/forgot-password');
    },

    async handleSsoMessage(event: MessageEvent) {
      // SECURITY: Check if the sender is in the whitelist or is 'self'
      const selfOrigin = window.location.origin;
      const isTrusted = isOriginTrusted(event.origin, this.trustedIFrameOrigins) || event.origin === selfOrigin;

      if (!isTrusted) {
        console.warn('Security blocked: message from untrusted origin', event.origin);
        return;
      }

      // Process the token only after origin validation
      const { ssoToken } = event.data;
      if (ssoToken) {
        // Wait for auth check to complete, then skip SSO if user is already authenticated
        await new Promise<void>((resolve) => {
          const check = () => (userModule.getters.isAuthChecked() ? resolve() : setTimeout(check, 50));
          check();
        });

        if (userModule.getters.current()) return;

        try {
          await loginWithSso(ssoToken);
          window.location.replace((this.$route.query.redirect as string) || '/');
        } catch (error) {
          console.error('SSO Login failed', error);
        }
      }
    },

    async processSidebarHeader(to: RouteLocationNormalized, from: RouteLocationNormalized) {
      const toConfig: ISidebarHeaderConfig | undefined = sidebarHeaderConfigs.find(
        (v: ISidebarHeaderConfig) => to.matched.some((m) => m.path.startsWith(v.routeStartsWith)),
      );

      const fromConfig: ISidebarHeaderConfig | undefined = sidebarHeaderConfigs.find(
        (v: ISidebarHeaderConfig) => from.matched.some((m) => m.path.startsWith(v.routeStartsWith)),
      );

      if (toConfig && toConfig.routeStartsWith !== fromConfig?.routeStartsWith) {
        this.assignSidebarHeader(toConfig, to);
      } else if (!toConfig) {
        main.actions.setSidebarHeaderData(null);
      }
    },

    async assignSidebarHeader(config: ISidebarHeaderConfig, route: RouteLocationNormalized) {
      const matchedPath = route.matched.find((r) => r.path.startsWith(config.routeStartsWith));
      const paramIdx = matchedPath?.path.split('/').findIndex((p) => p === config.routeParamName) || -1;
      const paramValue = this.$route.path.split('/')[paramIdx];
      const response = await config.setHeaderCallback(paramValue);

      main.actions.setSidebarHeaderData(response);
    },

    /**
     * Initialize client ID synchronization using localStorage + visibility events.
     * Falls back to existing polling if required APIs are not supported.
    */
    initClientIdSync() {
      // Checks if localStorage and visibility APIs are supported.
      const hasSyncSupport = () => {
        try {
          const featureVerificationKey = '__storage__verify__';
          window.localStorage.setItem(featureVerificationKey, '1');
          window.localStorage.removeItem(featureVerificationKey);
          return typeof document !== 'undefined' && typeof document.hidden !== 'undefined';
        } catch {
          return false;
        }
      };

      if (hasSyncSupport()) {
        this.ensureClientIdStored();
        document.addEventListener('visibilitychange', this.handleSyncEvents, false);
        window.addEventListener('storage', this.handleSyncEvents, false);
      } else {
        // Fallback to legacy polling
        this.runClientIdCheckPoll();
      }
    },

    /** Safely writes current client id to localStorage if logged in and not already set or outdated */
    ensureClientIdStored() {
      try {
        if (!this.$rsGlobals?.$currentUser?.client?.id) return;
        const current = String(this.$rsGlobals.$currentUser.client.id);
        const stored = window.localStorage.getItem('currentClientId');
        if (stored !== current) {
          window.localStorage.setItem('currentClientId', current);
        }
      } catch (error) {
        console.error('Failed to update client ID in localStorage:', error);
      }
    },

    /** Unified handler for visibility and storage events */
    async handleSyncEvents(e?: Event | StorageEvent) {
      if (document.hidden) return;
      if (e && 'key' in e && e.key !== 'currentClientId') return;

      try {
        if (!this.$rsGlobals?.$currentUser?.client?.id) return;
        const stored = window.localStorage.getItem('currentClientId');
        const clientIdInMemory = String(this.$rsGlobals.$currentUser.client.id);
        if (!stored) {
          window.localStorage.setItem('currentClientId', clientIdInMemory);
          return;
        }
        if (stored !== clientIdInMemory) {
          this.showClientMismatchWarning();
        }
      } catch (error) {
        console.error('Failed to check client ID mismatch:', error);
      }
    },

    /** Presents the mismatch dialog exactly as legacy polling did */
    showClientMismatchWarning() {
      this.$rsGlobals.$rsSwal.warning({
        title: 'Session Expiration Warning',
        text: 'This session has expired because you currently have another session open with a different client. Click ‘Okay’ to refresh the page and access your most current client.',
        confirmButtonText: `Okay`,
        showCloseButton: false,
        allowOutsideClick: false,
        confirmHandler: () => {
          if (Object.keys(this.$route.params || {}).length > 0) {
            window.location.replace('/');
            return;
          }
          window.location.reload();
        },
      });
    },

    /**
     * Runs every 30 seconds to fetch the current client ID for the session
     * and compare it with the in-memory client ID. If there's a mismatch,
     * the user is prompted to refresh the page to resynchronize the data.
     * This ensures consistency across multiple tabs in the same session.
    */
    async runClientIdCheckPoll() {
      try {
        const response = await this.getCurrentClient(undefined);
        if (this.$rsGlobals.$currentUser && (response.data.currentClientId !== this.$rsGlobals.$currentUser.client.id)) {
          this.showClientMismatchWarning();
          return;
        }
      } catch (error) {
        console.error('Client ID check failed:', error);
      }

      setTimeout(() => {
        this.runClientIdCheckPoll();
      }, clientIdPollIntervalMS);
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';

#app {
  height: 100dvh;
  background: $secondary-color-dark;

  &.rs-app-iframe {
    background: #ffffff;
  }

  .app-splash {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    height: 100vh;
    z-index: 1000;
    background: $secondary-color-dark;
    color: $white;

    img {
      width: 250px;
    }
  }
}

.rs-main-spin {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  height: 100%;
  width: 100%;
  align-items: flex-start;
  margin-top: 35vh;
  justify-content: center;
  z-index: 3000;
  background: rgba(0, 0, 0, 0);

  .rs-spin {
    background: none;
    padding: 1rem;
    border-radius: 0.5rem;
  }
}

.toast-text{
  vertical-align: text-bottom;
}

</style>
